/* eslint-disable camelcase */
import { Grid } from '@mui/material';
import { HexColor } from '@nx-smartmonkey/shared/domain';
import { RoutalColorPicker, RoutalDialog, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';

import CrossIcon from '../../../assets/svg/CrossIcon';
import { GeoFence } from '../../../domain/geoFence/GeoFence';
import { Project } from '../../../domain/project/Project';
import { useRetrieveActivePlan } from '../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanGeoFences } from '../../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useUpdateActivePlanGeoFences } from '../../../hooks/plan/useUpdateActivePlanGeoFences';
import { useCreateProjectGeoFence } from '../../../hooks/project/useCreateProjectGeoFence';
import { GeoFenceMapper } from '../../../mappers/geoFence/GeoFenceMapper';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';

type PlannerGeoFencesCreateDialogProps = {
  project: Project;
  newGeoFence: GeoFence;
  setNewGeoFence: (newGeoFence?: GeoFence) => void;
  onClose: (created: boolean) => void;
};

export const PlannerGeoFencesCreateDialog = ({
  project,
  newGeoFence,
  setNewGeoFence,
  onClose,
}: PlannerGeoFencesCreateDialogProps) => {
  const { data: activePlan } = useRetrieveActivePlan();
  const { run: updatePlanGeoFences } = useUpdateActivePlanGeoFences();
  const { data: activePlanGeoFences } = useRetrieveActivePlanGeoFences();
  const { run: createGeoFence, data: createdGeoFence, success } = useCreateProjectGeoFence({ project });

  useEffect(() => {
    if (success) {
      if (createdGeoFence !== undefined && activePlan !== undefined) {
        const newGeoPlanFences: GeoFence[] = [...(activePlanGeoFences ?? []), createdGeoFence];
        updatePlanGeoFences({
          geo_fences: newGeoPlanFences,
        });
      }
      onClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  if (!project || !newGeoFence) return null;

  const onSubmitGeoFence = () => {
    createGeoFence({ geoFence: newGeoFence! });
  };

  return (
    <RoutalDialog
      open
      dialogProps={{
        maxWidth: `sm`,
      }}
      onCloseDialog={() => {
        onClose(false);
      }}
      showCrossCloseButton={false}
      titleContent={<Text variant="h6" intlId="planner.zones.create_geo_fence" />}
      titleRightContent={
        <Grid item container justifyContent="flex-end" alignItems="center" wrap="nowrap">
          <Grid item>
            <Button
              onClick={onSubmitGeoFence}
              color="primary"
              disabled={(newGeoFence!.label ?? ``) === ``}
              intlId="planner.zones.create_geo_fence"
            />
          </Grid>

          <Grid item>
            <CrossIcon
              style={{
                color: `#211915`,
                fontSize: `14px`,
                marginLeft: `12px`,
                cursor: `pointer`,
              }}
              onClick={() => {
                onClose(false);
              }}
            />
          </Grid>
        </Grid>
      }
      showFooter={false}
    >
      <div style={{ position: `relative` }}>
        <InputText
          inputContainerStyles={{
            width: `100%`,
            backgroundColor: `white`,
            paddingLeft: `50px`,
          }}
          autoFocus
          inputStyles={{
            color: `#393939`,
            lineHeight: 0,
          }}
          fieldValue={newGeoFence!.label}
          onChange={(label) => {
            const newLabel: string = (label ?? ``).trim().replace(/\s{2,}/g, ` `);
            newGeoFence!.label = newLabel;
            setNewGeoFence(GeoFenceMapper.cloneDomain(newGeoFence));
          }}
          handleEnter={(label: any) => {
            const newLabel: string = (label ?? ``).trim().replace(/\s{2,}/g, ` `);
            newGeoFence!.label = newLabel;
            setNewGeoFence(GeoFenceMapper.cloneDomain(newGeoFence));
          }}
          placeholderIntlId="planner.zones.geo_fence_name"
        />

        <div
          style={{
            backgroundColor: `white`,
            position: `absolute`,
            left: `1px`,
            top: `1px`,
            height: `34px`,
            width: `50px`,
            borderTopLeftRadius: `4px`,
            borderBottomLeftRadius: `4px`,
          }}
        >
          <div
            style={{
              borderRadius: `50%`,
              position: `relative`,
              top: `4px`,
              left: `4px`,
            }}
          >
            <RoutalColorPicker
              color={newGeoFence!.color.value}
              containerStyles={{ position: `fixed` }}
              colorSampleStyles={{
                padding: `2px 8px`,
                margin: `0 2px`,
                color: `white`,
                borderRadius: `4px`,
              }}
              colorSampleContent={
                <Text tagName="span" style={{ color: `white`, cursor: `pointer`, fontSize: 10 }}>
                  {newGeoFence!.getShortLabel()}
                </Text>
              }
              onUpdate={(color: string) => {
                newGeoFence!.color = HexColor.create(color);
                setNewGeoFence(GeoFenceMapper.cloneDomain(newGeoFence));
              }}
            />
          </div>
          <div
            style={{
              margin: `6px 0`,
              width: `1px`,
              height: `calc(100% - 12px)`,
              backgroundColor: `#AAAAAA`,
              top: `0px`,
              position: `absolute`,
              left: `50px`,
            }}
          />
        </div>
      </div>

      <VSpacer large />
    </RoutalDialog>
  );
};
